'use client';

import { Box, Container } from '@mui/material';

import {
  BreakingMarketItem,
  BreakingMarketsListSkeleton,
  EmptyState,
  ErrorState,
} from '@/components';
import type { BreakingNewsMarket } from '@/types/breaking.types';

interface BreakingMarketsListProps {
  markets: BreakingNewsMarket[];
  isLoading: boolean;
  error: unknown;
  onRetry?: () => void;
}

export function BreakingMarketsList({
  markets,
  isLoading,
  error,
  onRetry,
}: BreakingMarketsListProps) {
  if (isLoading) {
    return <BreakingMarketsListSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <ErrorState
          title="Failed to load breaking markets"
          message="We couldn't load the breaking markets. Please try again."
          onRetry={onRetry}
        />
      </Container>
    );
  }

  if (!markets || markets.length === 0) {
    return (
      <Box sx={{ py: 4 }}>
        <EmptyState
          title="No breaking markets found"
          description="There are no breaking markets available at the moment."
        />
      </Box>
    );
  }

  const renderMarket = (market: BreakingNewsMarket, index: number) => {
    return <BreakingMarketItem key={market?.market_id} market={market} rank={index + 1} />;
  };

  const renderMarketList = markets?.map(renderMarket);

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{renderMarketList}</Box>;
}
